"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.profileIntervalEnd = exports.profileIntervalBegin = void 0;
const triggerBeacon_1 = __importDefault(require("../beacon/triggerBeacon"));
const events_1 = require("../constants/events");
const createDynamicEndEvent_1 = __importDefault(require("../events/createDynamicEndEvent"));
const createEndEvent_1 = __importDefault(require("../events/createEndEvent"));
/**
 * Begin profiling a dynamic event interval `eventName`: Mark the start time so
 * the duration can be measured later with `profileIntervalEnd()`.
 *
 * Returns `true` if successful, `false` otherwise.
 *
 * _**Note:** Dynamic events can be profiled multiple times per session, but
 * only one interval can be active at once. Attempts to begin profiling an
 * interval before the previous one has ended will be ignored._
 */
function profileIntervalBegin(profiler, eventName) {
    // Bail if we're not in a browser environment. See PERF-802.
    if (typeof window === 'undefined')
        return false;
    const { performance: perf } = window;
    const { eventStartQueue } = profiler;
    const isActiveEvent = perf.getEntriesByName(eventName).length > 0;
    // Bail if no event name provided or if the event is already active
    if (!eventName || isActiveEvent)
        return false;
    // Mark the interval start time in the browser's performance timeline and
    // queue the event start name to be sent with the next beacon.
    perf.mark(eventName);
    eventStartQueue.push(eventName);
    // Return success!
    return true;
}
exports.profileIntervalBegin = profileIntervalBegin;
/**
 * End profiling a dynamic event interval `eventName` started previously with
 * `profileIntervalBegin()`: Mark the end time of the event, measure the
 * interval duration, and trigger a beacon immediately.
 *
 * A custom `startEventName` can be provided to use a different event's start
 * time to measure the interval. This can be useful for when multiple dynamic
 * events share the same start time, e.g., relative to when a primary component
 * has loaded.
 *
 * If `isFailure` is `true`, set the event's interval duration to -1 and trigger
 * a beacon. Useful for indicating a failure, e.g., the profile target fails to
 * load.
 *
 * Returns `true` if successful, `false` otherwise.
 */
function profileIntervalEnd(profiler, eventName, { isFailure, startEventName, } = { isFailure: false }) {
    // Bail if we're not in a browser environment. See PERF-802.
    if (typeof window === 'undefined')
        return false;
    const { performance: perf } = window;
    const { eventEndQueue } = profiler;
    const dynamicEventEndObj = isFailure
        ? (0, createEndEvent_1.default)(eventName, events_1.ERROR_METRIC_VALUE)
        : (0, createDynamicEndEvent_1.default)(eventName, startEventName);
    // Bail if the dynamic end event couldn't be created (e.g., if the start
    // event wasn't found)
    if (!dynamicEventEndObj)
        return false;
    eventEndQueue.push(dynamicEventEndObj);
    const beaconSuccess = (0, triggerBeacon_1.default)(profiler);
    // Clear out event name after beaconing so it can be used again later
    perf.clearMarks(eventName);
    return beaconSuccess;
}
exports.profileIntervalEnd = profileIntervalEnd;
